import { apiRequest } from "@/lib/api";

export type CloudWalletAccountRecord = {
  id: string;
  display_name: string;
  provider_slug: string;
  provider_name: string;
  api_key?: string;
  base_url?: string;
  balance_path?: string;
  api_key_header?: string;
  api_key_location?: string;
  api_key_query_param?: string;
  request_method?: string;
  request_body?: Record<string, unknown>;
  response_data_path?: string;
  currency_hint?: string;
  balance_field_map?: Record<string, string>;
  active: boolean;
  created_at: string;
  updated_at: string;
};

export type CloudWalletAccountPublic = Omit<CloudWalletAccountRecord, "api_key"> & {
  has_api_key: boolean;
  api_key_mask?: string;
};

export type CloudWalletBalanceSnapshot = {
  account_id: string;
  display_name: string;
  provider_slug: string;
  provider_name: string;
  active: boolean;
  status: string;
  credit: number;
  threshold: number;
  currency: string;
  can_create_instance?: boolean;
  fetched_at_utc?: string;
  error?: string;
  burn_per_day?: number;
  spend_7d?: number;
  history?: Array<{ d: string; balance: number }>;
};

export type CloudWalletSummaryResponse = {
  fetched_at_utc: string;
  configured_accounts: number;
  active_accounts: number;
  healthy_accounts: number;
  failed_accounts: number;
  launchable_accounts: number;
  blocked_accounts: number;
  provider_count: number;
  total_credits_by_currency: Array<{ currency: string; credit: number }>;
  accounts: CloudWalletBalanceSnapshot[];
};

export type CloudWalletDashboardCacheResponse = {
  cached: boolean;
  updated_at: string;
  ttl_seconds?: number;
  accounts: CloudWalletAccountPublic[];
  summary: CloudWalletSummaryResponse;
};

export type CloudWalletAccountUpsert = Partial<CloudWalletAccountRecord> & {
  id?: string;
  display_name: string;
  provider_slug: string;
  provider_name: string;
};

function slugify(raw: string): string {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function maskSecret(secret: string): string {
  const trimmed = String(secret || "").trim();
  if (!trimmed) return "";
  if (trimmed.length <= 4) return "*".repeat(trimmed.length);
  return `${"*".repeat(trimmed.length - 4)}${trimmed.slice(-4)}`;
}

function randomID(prefix = "wallet"): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}_${crypto.randomUUID().replace(/-/g, "")}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 14)}`;
}

function normalizeMap(input?: Record<string, string>): Record<string, string> | undefined {
  if (!input || typeof input !== "object") return undefined;
  const out = Object.entries(input).reduce<Record<string, string>>((acc, [key, value]) => {
    const normalizedKey = slugify(key);
    const normalizedValue = String(value || "").trim();
    if (!normalizedKey || !normalizedValue) return acc;
    acc[normalizedKey] = normalizedValue;
    return acc;
  }, {});
  return Object.keys(out).length > 0 ? out : undefined;
}

export function cloudWalletDefaultBaseURL(providerSlug: string): string {
  switch (slugify(providerSlug)) {
    case "aws":
    case "amazon_web_services":
    case "amazonwebservices":
      return "https://sts.amazonaws.com";
    case "google_cloud":
    case "googlecloud":
    case "gcp":
      return "https://cloudbilling.googleapis.com";
    case "hyperstack":
      return "https://infrahub-api.nexgencloud.com/v1";
    case "runpod":
      return "https://api.runpod.io";
    case "lambda_cloud":
    case "lambda":
    case "lambdacloud":
      return "https://cloud.lambda.ai";
    case "vast_ai":
    case "vast":
    case "vastai":
      return "https://console.vast.ai";
    case "nscale":
    case "nscale_ai":
    case "nscaleai":
      return "https://identity.nks.europe-west4.nscale.com";
    default:
      return "";
  }
}

function cloudWalletDefaultBalancePath(providerSlug: string): string {
  switch (slugify(providerSlug)) {
    case "aws":
    case "amazon_web_services":
    case "amazonwebservices":
      return "/";
    case "google_cloud":
    case "googlecloud":
    case "gcp":
      return "/v1/billingAccounts";
    case "hyperstack":
      return "/billing/user-credit/credit";
    case "runpod":
      return "/graphql";
    case "vast_ai":
    case "vast":
    case "vastai":
      return "/api/v0/users/current/";
    case "lambda_cloud":
    case "lambda":
    case "lambdacloud":
      return "/api/v1/regions";
    case "nscale":
    case "nscale_ai":
    case "nscaleai":
      return "/api/v1/organizations/{organization_id}/quotas";
    default:
      return "/v1/billing/user-credit/credit";
  }
}

function cloudWalletDefaultAPIKeyHeader(providerSlug: string): string {
  switch (slugify(providerSlug)) {
    case "hyperstack":
      return "api_key";
    default:
      return "Authorization";
  }
}

function cloudWalletDefaultAPIKeyLocation(providerSlug: string): string {
  switch (slugify(providerSlug)) {
    case "runpod":
      return "query";
    default:
      return "header";
  }
}

function cloudWalletDefaultAPIKeyQueryParam(providerSlug: string): string {
  switch (slugify(providerSlug)) {
    case "runpod":
      return "api_key";
    default:
      return "api_key";
  }
}

function cloudWalletDefaultRequestMethod(providerSlug: string): string {
  switch (slugify(providerSlug)) {
    case "runpod":
      return "POST";
    default:
      return "GET";
  }
}

function cloudWalletDefaultResponseDataPath(providerSlug: string): string {
  switch (slugify(providerSlug)) {
    case "runpod":
      return "data.myself";
    case "lambda_cloud":
    case "lambda":
    case "lambdacloud":
      return "data";
    default:
      return "";
  }
}

function cloudWalletDefaultRequestBody(providerSlug: string): Record<string, unknown> | undefined {
  switch (slugify(providerSlug)) {
    case "runpod":
      return {
        query: "query myself { myself { clientBalance minBalance } }",
      };
    default:
      return undefined;
  }
}

function cloudWalletDefaultBalanceFieldMap(providerSlug: string): Record<string, string> | undefined {
  switch (slugify(providerSlug)) {
    case "runpod":
      return {
        credit: "clientBalance",
        threshold: "minBalance",
      };
    case "vast_ai":
    case "vast":
    case "vastai":
      return {
        credit: "balance",
      };
    default:
      return undefined;
  }
}

export function normalizeCloudWalletAccount(input: Partial<CloudWalletAccountRecord>): CloudWalletAccountRecord {
  const providerSlug = slugify(String(input.provider_slug || input.provider_name || "custom"));
  const providerName = String(input.provider_name || providerSlug || "Custom").trim() || "Custom";
  const now = new Date().toISOString();
  return {
    id: String(input.id || "").trim() || randomID(),
    display_name: String(input.display_name || `${providerName} Wallet`).trim() || `${providerName} Wallet`,
    provider_slug: providerSlug,
    provider_name: providerName,
    api_key: String(input.api_key || "").trim() || undefined,
    base_url: String(input.base_url || cloudWalletDefaultBaseURL(providerSlug) || "").trim() || undefined,
    balance_path: String(input.balance_path || cloudWalletDefaultBalancePath(providerSlug) || "").trim() || undefined,
    api_key_header: String(input.api_key_header || cloudWalletDefaultAPIKeyHeader(providerSlug) || "").trim() || undefined,
    api_key_location: String(input.api_key_location || cloudWalletDefaultAPIKeyLocation(providerSlug) || "").trim() || undefined,
    api_key_query_param: String(input.api_key_query_param || cloudWalletDefaultAPIKeyQueryParam(providerSlug) || "").trim() || undefined,
    request_method: String(input.request_method || cloudWalletDefaultRequestMethod(providerSlug) || "").trim().toUpperCase() || undefined,
    request_body: input.request_body || cloudWalletDefaultRequestBody(providerSlug),
    response_data_path: String(input.response_data_path || cloudWalletDefaultResponseDataPath(providerSlug) || "").trim() || undefined,
    currency_hint: String(input.currency_hint || "USD").trim().toUpperCase() || "USD",
    balance_field_map: normalizeMap(input.balance_field_map) || cloudWalletDefaultBalanceFieldMap(providerSlug),
    active: input.active !== false,
    created_at: String(input.created_at || now),
    updated_at: String(input.updated_at || now),
  };
}

export function normalizeCloudWalletAccountPublic(input: Partial<CloudWalletAccountPublic>): CloudWalletAccountPublic {
  const record = normalizeCloudWalletAccount(input);
  return {
    ...record,
    has_api_key: typeof input.has_api_key === "boolean" ? input.has_api_key : !!record.api_key,
    api_key_mask: String(input.api_key_mask || "").trim() || (record.api_key ? maskSecret(record.api_key) : undefined),
  };
}

export async function readCloudWalletAccountsPublic(): Promise<CloudWalletAccountPublic[]> {
  const payload = await apiRequest<{ accounts?: Partial<CloudWalletAccountPublic>[] }>("/api/wallet/accounts", {
    timeoutMs: 15000,
  });
  const rows = Array.isArray(payload?.accounts) ? payload.accounts : [];
  return rows.map((entry) => normalizeCloudWalletAccountPublic(entry));
}

export async function readCloudWalletDashboardCache(): Promise<CloudWalletDashboardCacheResponse | null> {
  try {
    const payload = await apiRequest<{
      cached?: boolean;
      updated_at?: string;
      ttl_seconds?: number;
      accounts?: Partial<CloudWalletAccountPublic>[];
      summary?: CloudWalletSummaryResponse;
    }>("/api/wallet/summary/cache", {
      cache: "no-store",
      timeoutMs: 3000,
    });
    if (!payload?.summary) return null;
    return {
      cached: payload.cached === true,
      updated_at: String(payload.updated_at || payload.summary.fetched_at_utc || ""),
      ttl_seconds: Number(payload.ttl_seconds || 0) || undefined,
      accounts: (Array.isArray(payload.accounts) ? payload.accounts : []).map((entry) =>
        normalizeCloudWalletAccountPublic(entry),
      ),
      summary: payload.summary,
    };
  } catch {
    return null;
  }
}

export async function upsertCloudWalletAccount(payload: CloudWalletAccountUpsert): Promise<CloudWalletAccountPublic[]> {
  const normalized = normalizeCloudWalletAccount({
    ...payload,
    updated_at: new Date().toISOString(),
  });
  const response = await apiRequest<{ accounts?: Partial<CloudWalletAccountRecord>[] }>("/api/wallet/accounts", {
    method: "POST",
    body: normalized,
    timeoutMs: 20000,
  });
  const rows = Array.isArray(response?.accounts) ? response.accounts : [];
  return rows.map((entry) => normalizeCloudWalletAccountPublic(entry));
}

export async function deleteCloudWalletAccount(accountID: string): Promise<CloudWalletAccountPublic[]> {
  const response = await apiRequest<{ accounts?: Partial<CloudWalletAccountRecord>[] }>("/api/wallet/accounts", {
    method: "DELETE",
    body: { id: String(accountID || "").trim() },
    timeoutMs: 15000,
  });
  const rows = Array.isArray(response?.accounts) ? response.accounts : [];
  return rows.map((entry) => normalizeCloudWalletAccountPublic(entry));
}
